import { useCallback, useEffect, useRef, useState } from 'react'

export function useAutosave<T>(
  data: T,
  onSave: (data: T) => void,
  debounceMs: number = 500,
  /** When false, pending saves are cancelled and new saves are suppressed. Pass `isOpen` for modal usage. */
  isEnabled: boolean = true
): { lastSaveTime: number | null; isSaving: boolean; cancelPendingSave: () => void } {
  const [lastSaveTime, setLastSaveTime] = useState<number | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const isFirstRender = useRef(true)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dataRef = useRef(data)
  const onSaveRef = useRef(onSave)
  const previousDataRef = useRef<string>('')

  dataRef.current = data
  onSaveRef.current = onSave

  const serializedData = JSON.stringify(data)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      previousDataRef.current = serializedData
      return
    }

    if (!isEnabled) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        setIsSaving(false)
      }
      // Keep baseline current so re-enabling doesn't see a stale diff
      previousDataRef.current = serializedData
      return
    }

    if (previousDataRef.current === serializedData) {
      return
    }

    previousDataRef.current = serializedData

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setIsSaving(true)

    timeoutRef.current = setTimeout(() => {
      onSaveRef.current(dataRef.current)
      setLastSaveTime(Date.now())
      setIsSaving(false)
    }, debounceMs)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [serializedData, debounceMs, isEnabled])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const cancelPendingSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsSaving(false)
  }, [])

  return { lastSaveTime, isSaving, cancelPendingSave }
}
